import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Camera, CheckCircle2, Loader2, MapPin, RefreshCw, RotateCcw,
  UploadCloud, WifiOff, X,
} from "lucide-react";

import { uploadDeliveryProofPhoto } from "../../services/assetUploadService";
import { createClientRequestId } from "../../utils/clientRequestId";
import {
  RIDER_PROOF_MIME_TYPES,
  riderApi,
  validateRiderProofFile,
} from "../services/riderApi";
import {
  deletePendingDeliveryProof,
  recoverPendingDeliveryProof,
  savePendingDeliveryProof,
} from "../proof/pendingDeliveryProof";
import { getRiderDeviceInstanceId } from "../utils/riderDevice";
import { readRiderExecutionAuthority } from "../utils/riderExecutionAuthority";

const FAILURE_REASONS = [
  ["RECIPIENT_UNAVAILABLE", "Recipient is unavailable"],
  ["CUSTOMER_UNREACHABLE", "Customer cannot be reached"],
  ["ADDRESS_NOT_FOUND", "Address cannot be found"],
  ["CUSTOMER_REQUESTED_RESCHEDULE", "Customer requested another schedule"],
  ["COD_NOT_PROVIDED", "Cash payment was not provided"],
  ["UNSAFE_OR_INACCESSIBLE", "Location is unsafe or inaccessible"],
  ["PARCEL_DAMAGED", "Parcel is damaged"],
  ["OTHER", "Another problem"],
];

const ACTIONS = {
  RECIPIENT_UNAVAILABLE: ["SAME_RUN_RETRY", "RESCHEDULE", "MANAGEMENT_REVIEW", "TERMINAL_FAILURE_REVIEW"],
  CUSTOMER_UNREACHABLE: ["SAME_RUN_RETRY", "RESCHEDULE", "MANAGEMENT_REVIEW", "TERMINAL_FAILURE_REVIEW"],
  ADDRESS_NOT_FOUND: ["SAME_RUN_RETRY", "RESCHEDULE", "RETURN_TO_DISPATCH", "MANAGEMENT_REVIEW"],
  CUSTOMER_REQUESTED_RESCHEDULE: ["RESCHEDULE"],
  COD_NOT_PROVIDED: ["SAME_RUN_RETRY", "RESCHEDULE", "MANAGEMENT_REVIEW", "TERMINAL_FAILURE_REVIEW"],
  UNSAFE_OR_INACCESSIBLE: ["SAME_RUN_RETRY", "RESCHEDULE", "RETURN_TO_DISPATCH", "MANAGEMENT_REVIEW"],
  PARCEL_DAMAGED: ["RETURN_TO_DISPATCH", "CUSTODY_RETURN", "MANAGEMENT_REVIEW", "TERMINAL_FAILURE_REVIEW"],
  OTHER: ["SAME_RUN_RETRY", "RESCHEDULE", "RETURN_TO_DISPATCH", "CUSTODY_RETURN", "MANAGEMENT_REVIEW", "TERMINAL_FAILURE_REVIEW"],
};

const ACTION_LABELS = {
  SAME_RUN_RETRY: "Try this stop again on this run",
  RESCHEDULE: "Return parcel and reschedule",
  RETURN_TO_DISPATCH: "Return parcel to dispatch",
  CUSTODY_RETURN: "Record custody return",
  MANAGEMENT_REVIEW: "Send to management review",
  TERMINAL_FAILURE_REVIEW: "Request terminal-failure review",
};

const CONTACT_OUTCOMES = [
  ["RECIPIENT_CONTACTED", "Spoke with recipient"],
  ["PHONE_ANSWERED", "Phone answered"],
  ["PHONE_NO_ANSWER", "No answer"],
  ["PHONE_UNREACHABLE", "Phone unreachable"],
  ["MESSAGE_SENT", "Message sent"],
  ["NOT_ATTEMPTED", "Contact not attempted"],
];

const RESCHEDULE_INPUT_MAX = "2038-01-19T03:14";
const RESCHEDULE_STORAGE_MAX_MS = Date.parse("2038-01-19T03:14:07.999Z");

function positionError(error) {
  if (error?.code === 1) {
    return {
      code: "GPS_PERMISSION_DENIED",
      message: "Location permission was denied. You may continue, but the proof will require review.",
      flags: { locationPermissionDenied: true },
    };
  }
  return {
    code: error?.code === 2 ? "GPS_UNAVAILABLE" : "GPS_DEVICE_ERROR",
    message: "Location is unavailable. You may continue, but the proof will require review.",
    flags: error?.code === 2 ? { locationUnavailable: true } : {},
  };
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(Object.assign(new Error("Location is unavailable"), { code: 2 }));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 30000,
    });
  });
}

function locationPresentation(location) {
  if (!location) return null;
  if (location.message) return { tone: "amber", text: location.message };
  if (location.accuracyM <= 50) return { tone: "emerald", text: `Location captured within about ${Math.round(location.accuracyM)} m.` };
  if (location.accuracyM <= 150) return { tone: "amber", text: `Location accuracy is about ${Math.round(location.accuracyM)} m. This is usable but less precise.` };
  return { tone: "amber", text: `Location accuracy is about ${Math.round(location.accuracyM)} m. Continuing will send the proof for review.` };
}

function friendlyError(rawError) {
  // The photo upload goes through the shared API client, which keeps the
  // server's code under response.data; Rider API errors carry it directly.
  const serverData = rawError?.response?.data || {};
  const error = { ...rawError, code: serverData.code || rawError?.code, message: serverData.message || serverData.msg || rawError?.message };
  if (["DELIVERY_ATTEMPT_CONFLICT", "DELIVERY_ATTEMPT_NOT_ELIGIBLE", "DELIVERY_PROOF_NOT_ELIGIBLE"].includes(error?.code)) {
    return "This stop changed while you were working. Refresh the run before trying again.";
  }
  if (error?.code === "DELIVERY_ATTEMPT_LIMIT_REACHED") return "Another attempt needs management approval.";
  if (error?.code === "COD_COLLECTION_REQUIRED") return "Enter the cash received before completing this delivery.";
  if (["COD_VERSION_CONFLICT", "COD_INVALID_TRANSITION", "COD_LEDGER_DRIFT"].includes(error?.code)) return "The COD record changed. Refresh the run before trying again.";
  if (error?.code === "COD_EXPECTATION_MISSING") return "This older COD shipment has no cash expectation and cannot be completed through this workflow. Contact dispatch.";
  if (error?.code === "DELIVERY_PROOF_AUTHORITY_STALE") return "This photo was taken while another device was in control. Take it again on this device to finish the delivery.";
  if (error?.code === "DELIVERY_PROOF_EXISTS") return "This delivery's photo is already saved. Refresh the trip to continue.";
  if (error?.code === "DELIVERY_PROOF_REQUIRED") return "The finalized proof is no longer available. Refresh before continuing.";
  return error?.message || "We could not save this outcome. Check your connection and try again.";
}

function formatPhp(cents) {
  return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(cents || 0) / 100);
}

function pesoInputToCents(value) {
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(String(value || "").trim());
  if (!match) return null;
  const cents = (Number(match[1]) * 100) + Number((match[2] || "").padEnd(2, "0"));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

function expectedVersions(run, stop) {
  return {
    expectedAttemptVersion: stop.attempt.version,
    expectedStopVersion: stop.version,
    expectedShipmentVersion: stop.shipmentVersion,
    expectedAssignmentVersion: stop.assignmentVersion,
    expectedRunVersion: run.version,
  };
}

export default function RiderAttemptOutcomePanel({
  businessId, run, stop, executionHeaders = null, executionEnabled = true, onUpdated,
}) {
  const [mode, setMode] = useState("proof");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [capturedAt, setCapturedAt] = useState("");
  const [capturedOffline, setCapturedOffline] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [location, setLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [acceptPoorGps, setAcceptPoorGps] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientType, setRecipientType] = useState("");
  const [recipientRelationship, setRecipientRelationship] = useState("");
  const [receivingLocation, setReceivingLocation] = useState("");
  const [authorizationEvidence, setAuthorizationEvidence] = useState("");
  const [recipientNote, setRecipientNote] = useState("");
  // A proof the server already holds for this attempt. The app can reload at
  // any point (on Android, opening the camera may do it), and asking again
  // for a finalized photo is refused.
  const serverProof = stop.attempt?.proof?.id ? stop.attempt.proof : null;
  const [proof, setProof] = useState(serverProof);
  useEffect(() => {
    if (serverProof) setProof((current) => current || serverProof);
  }, [serverProof?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [uploadStage, setUploadStage] = useState("");
  const [retriedAfterNetworkError, setRetriedAfterNetworkError] = useState(false);
  const [confirmDelivery, setConfirmDelivery] = useState(false);
  const [cashAmount, setCashAmount] = useState(() => (
    stop.cod?.expectedAmountCents == null ? "" : (Number(stop.cod.expectedAmountCents) / 100).toFixed(2)
  ));
  const [reason, setReason] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [explanation, setExplanation] = useState("");
  const [notes, setNotes] = useState("");
  const [contacts, setContacts] = useState([]);
  const [rescheduledNotBefore, setRescheduledNotBefore] = useState("");
  const [rescheduledNotAfter, setRescheduledNotAfter] = useState("");
  const [custodyConfirmed, setCustodyConfirmed] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const completeKey = useRef(createClientRequestId(`complete_attempt_${stop.attempt.id}`));
  const failKey = useRef(createClientRequestId(`fail_attempt_${stop.attempt.id}`));
  const collectKey = useRef(createClientRequestId(`collect_cod_${stop.attempt.id}`));

  const actions = ACTIONS[reason] || [];
  const needsCustodyReturn = ["RESCHEDULE", "RETURN_TO_DISPATCH", "CUSTODY_RETURN"].includes(nextAction);
  const locationInfo = locationPresentation(location);
  const cashAmountCents = pesoInputToCents(cashAmount);
  const expectedCashCents = Number(stop.cod?.expectedAmountCents || 0);
  const cashDifferenceCents = cashAmountCents == null ? null : cashAmountCents - expectedCashCents;
  const targetV2 = Number(run.workflowVersion) === 2;
  const pendingBinding = useMemo(() => {
    if (!targetV2) return null;
    const authority = readRiderExecutionAuthority(businessId, run.id);
    if (!authority?.authorityId || !authority?.authorityVersion) return null;
    return {
      businessId,
      runId: run.id,
      attemptId: stop.attempt.id,
      authorityId: authority.authorityId,
      authorityVersion: authority.authorityVersion,
      deviceInstanceId: getRiderDeviceInstanceId(),
    };
  }, [businessId, run.id, stop.attempt.id, targetV2]);
  const recipientValid = !targetV2 || recipientType === "CUSTOMER"
    || (recipientType === "HOUSEHOLD_MEMBER" && recipientName.trim() && recipientRelationship.trim())
    || (recipientType === "GUARD_RECEPTION" && recipientName.trim() && receivingLocation.trim())
    || (recipientType === "AUTHORIZED_REPRESENTATIVE" && recipientName.trim() && authorizationEvidence.trim());

  useEffect(() => {
    const wentOnline = () => setOnline(true);
    const wentOffline = () => setOnline(false);
    window.addEventListener("online", wentOnline);
    window.addEventListener("offline", wentOffline);
    return () => {
      window.removeEventListener("online", wentOnline);
      window.removeEventListener("offline", wentOffline);
    };
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!pendingBinding || file || proof) return undefined;
    let active = true;
    recoverPendingDeliveryProof(pendingBinding).then((recovered) => {
      if (!active || !recovered) return;
      setFile(recovered.file);
      setCapturedAt(recovered.capturedAt);
      setCapturedOffline(true);
      setPreviewUrl(URL.createObjectURL(recovered.file));
      captureLocation();
    }).catch(() => {
      if (active) setError("Protected photo recovery is unavailable. A new photo can still be kept on this screen.");
    });
    return () => { active = false; };
  }, [file, pendingBinding, proof]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectFile = async (selected) => {
    setError("");
    try {
      validateRiderProofFile(selected);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setFile(selected);
      setPreviewUrl(URL.createObjectURL(selected));
      setCapturedAt(new Date().toISOString());
      setCapturedOffline(navigator.onLine === false);
      setProof(null);
      setConfirmDelivery(false);
      // The phone already knows where it is; the Rider is not a sensor. Each
      // photo takes a fresh fix, and "Capture location again" stays for retries.
      captureLocation();
      if (pendingBinding) {
        try {
          await savePendingDeliveryProof(selected, pendingBinding, new Date().toISOString());
        } catch {
          setError("The photo is selected, but protected recovery is unavailable. Keep this screen open until upload completes.");
        }
      }
    } catch (selectionError) {
      setError(selectionError.message);
    }
  };

  const captureLocation = async () => {
    setLocating(true);
    setError("");
    setAcceptPoorGps(false);
    try {
      const result = await getPosition();
      setLocation({
        latitude: result.coords.latitude,
        longitude: result.coords.longitude,
        accuracyM: result.coords.accuracy,
        exceptionCode: result.coords.accuracy > 150 ? "GPS_POOR_ACCURACY_ACCEPTED" : null,
      });
    } catch (requestError) {
      const fallback = positionError(requestError);
      setLocation({ exceptionCode: fallback.code, message: fallback.message, flags: fallback.flags });
    } finally {
      setLocating(false);
    }
  };

  const uploadProof = async () => {
    setError("");
    try {
      validateRiderProofFile(file);
      if (!location) throw new Error("Capture location or record a location exception before uploading.");
      if (location.accuracyM > 150 && !acceptPoorGps) {
        throw new Error("Confirm that you want to continue with poor location accuracy.");
      }
      if (!online) throw new Error("You are offline. Keep this screen open and retry when your connection returns. The delivery has not been completed.");
      setBusy("upload");
      const result = await uploadDeliveryProofPhoto(file, {
        businessId,
        attemptId: stop.attempt.id,
        deviceCapturedAt: capturedAt,
        capturedLatitude: location.latitude ?? null,
        capturedLongitude: location.longitude ?? null,
        accuracyM: location.accuracyM ?? null,
        gpsExceptionCode: location.exceptionCode || null,
        recipientType: targetV2 ? recipientType : null,
        recipientName,
        recipientRelationship: targetV2 ? recipientRelationship : null,
        receivingLocation: targetV2 ? receivingLocation : null,
        authorizationEvidence: targetV2 ? authorizationEvidence : null,
        recipientNote,
        deviceExceptionFlags: {
          ...(location.flags || {}),
          ...(capturedOffline ? { capturedOffline: true } : {}),
          ...(retriedAfterNetworkError ? { retriedAfterNetworkError: true } : {}),
        },
      }, {
        resumeKey: `rider_proof_finalize_${businessId}_${stop.attempt.id}`,
        onStage: setUploadStage,
        headers: executionHeaders || {},
      });
      setProof(result.proof || result.data?.proof);
      if (pendingBinding) await deletePendingDeliveryProof(pendingBinding);
    } catch (uploadError) {
      if (!uploadError?.code || uploadError.code === "ERR_NETWORK") setRetriedAfterNetworkError(true);
      setError(friendlyError(uploadError));
    } finally {
      setBusy("");
    }
  };

  // WP6 target-v2: cash is its own fact, recorded by its own command before the
  // delivery, so a completion that fails afterwards never loses the payment. The
  // Rider still confirms the moment once: "Delivered — ₱X received" sends both
  // commands in order. Collecting touches only the cash record, so the stop and
  // run versions the completion expects are unchanged by it.
  const deliverTarget = async () => {
    setBusy("complete");
    setError("");
    let cashRecorded = false;
    try {
      if (stop.isCashOnDelivery && stop.cod?.status === "DUE") {
        await riderApi.collectCod(businessId, stop.attempt.id, {
          codRecordId: stop.cod?.recordId,
          expectedCodVersion: stop.cod?.version,
          netDueCents: expectedCashCents,
          tenderedCents: cashAmountCents,
          idempotencyKey: collectKey.current,
        }, { executionHeaders });
        collectKey.current = createClientRequestId(`collect_cod_${stop.attempt.id}`);
        cashRecorded = true;
      }
      const result = await riderApi.completeAttempt(businessId, stop.attempt.id, {
        proofId: proof?.id,
        ...expectedVersions(run, stop),
        idempotencyKey: completeKey.current,
      }, { executionHeaders });
      completeKey.current = createClientRequestId(`complete_attempt_${stop.attempt.id}`);
      onUpdated(result, stop.isCashOnDelivery
        ? `Delivered. Keep the ${formatPhp(expectedCashCents)} for the store.`
        : "Delivered.");
    } catch (requestError) {
      setError(friendlyError(requestError));
      if (requestError?.code === "DELIVERY_PROOF_AUTHORITY_STALE") setProof(null);
      // The cash is safely recorded even though the delivery step failed. Reload
      // so the screen shows it and the next tap only completes the delivery.
      if (cashRecorded) onUpdated({}, "Cash recorded. The parcel is not marked delivered yet; tap Confirm delivered again.");
    } finally {
      setBusy("");
    }
  };

  const completeDelivery = async () => {
    setBusy("complete");
    setError("");
    try {
      const result = await riderApi.completeAttempt(businessId, stop.attempt.id, {
        proofId: proof?.id,
        // Legacy v1 only. On the target path the cash is already its own fact and
        // completion merely validates that it exists.
        ...(stop.isCashOnDelivery && !targetV2 ? {
          codCollection: {
            amountCents: cashAmountCents,
            expectedCodVersion: stop.cod?.version,
          },
        } : {}),
        ...expectedVersions(run, stop),
        idempotencyKey: completeKey.current,
      }, ...(targetV2 ? [{ executionHeaders }] : []));
      completeKey.current = createClientRequestId(`complete_attempt_${stop.attempt.id}`);
      onUpdated(result, stop.isCashOnDelivery
        ? "Parcel delivered and cash collected. Finance remittance is still pending."
        : "Delivery completed with finalized proof.");
    } catch (requestError) {
      setError(friendlyError(requestError));
      // The photo was taken while another device was in control, so it cannot
      // complete this delivery. Reopen the photo step on this device; the
      // recorded cash is unaffected.
      if (requestError?.code === "DELIVERY_PROOF_AUTHORITY_STALE") {
        setProof(null);
        setConfirmDelivery(false);
      }
    } finally {
      setBusy("");
    }
  };

  const toggleContact = (value) => {
    setContacts((current) => {
      if (value === "NOT_ATTEMPTED") return current.includes(value) ? [] : [value];
      const withoutNotAttempted = current.filter((item) => item !== "NOT_ATTEMPTED");
      return withoutNotAttempted.includes(value)
        ? withoutNotAttempted.filter((item) => item !== value)
        : [...withoutNotAttempted, value];
    });
  };

  const submitFailure = async () => {
    setBusy("failure");
    setError("");
    try {
      let rescheduleStart = null;
      let rescheduleEnd = null;
      if (!targetV2 && nextAction === "RESCHEDULE") {
        const before = new Date(rescheduledNotBefore);
        const after = new Date(rescheduledNotAfter);
        if (Number.isNaN(before.getTime()) || Number.isNaN(after.getTime())
          || before.getTime() <= Date.now() || after.getTime() <= before.getTime()
          || before.getTime() > RESCHEDULE_STORAGE_MAX_MS || after.getTime() > RESCHEDULE_STORAGE_MAX_MS) {
          throw new Error("Choose a valid future window ending no later than January 19, 2038.");
        }
        rescheduleStart = before.toISOString();
        rescheduleEnd = after.toISOString();
      }
      const result = await riderApi.failAttempt(businessId, stop.attempt.id, {
        reason,
        workflowVersion: run.workflowVersion,
        // The Rider no longer decides what happens to the Stop on the target
        // path; dispatch does. Sending a disposition would be refused.
        ...(targetV2 ? {} : {
          nextAction,
          rescheduledNotBefore: rescheduleStart,
          rescheduledNotAfter: rescheduleEnd,
          custodyDisposition: needsCustodyReturn && custodyConfirmed ? "RETURNED_TO_DISPATCH" : null,
        }),
        explanation,
        notes,
        contactOutcomes: contacts,
        ...expectedVersions(run, stop),
        idempotencyKey: failKey.current,
      }, ...(targetV2 ? [{ executionHeaders }] : []));
      failKey.current = createClientRequestId(`fail_attempt_${stop.attempt.id}`);
      onUpdated(result, targetV2
        ? "Problem recorded. Dispatch will decide what happens to this stop."
        : (nextAction === "SAME_RUN_RETRY" ? "A new attempt is ready for this stop." : "Delivery problem recorded."));
    } catch (requestError) {
      setError(friendlyError(requestError));
    } finally {
      setBusy("");
    }
  };

  const stageText = useMemo(() => ({
    preparing: "Preparing secure upload...",
    uploading: "Uploading photo...",
    finalizing: "Finalizing proof...",
    complete: "Proof finalized.",
  }[uploadStage] || "Saving..."), [uploadStage]);

  return (
    <section className="rounded-3xl border border-violet-200 bg-violet-50 p-5 dark:border-violet-500/20 dark:bg-violet-500/10">
      <div className="flex items-start gap-3">
        <Camera className="mt-0.5 h-6 w-6 shrink-0 text-violet-700 dark:text-violet-300" aria-hidden="true" />
        <div>
          <h2 className="text-lg font-semibold">Record the delivery outcome</h2>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
            {stop.isCashOnDelivery
              ? "Photo proof and the actual cash received are required before this cash-on-delivery parcel can be completed. Do not photograph faces."
              : "Photo proof is required before a non-cash delivery can be completed. Do not photograph faces."}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-white/80 p-1 dark:bg-black/20">
        <button type="button" onClick={() => setMode("proof")} className={`min-h-11 rounded-xl px-3 text-sm font-semibold ${mode === "proof" ? "bg-violet-700 text-white" : "text-stone-600 dark:text-stone-300"}`}>Delivered</button>
        <button type="button" onClick={() => setMode("failure")} className={`min-h-11 rounded-xl px-3 text-sm font-semibold ${mode === "failure" ? "bg-amber-700 text-white" : "text-stone-600 dark:text-stone-300"}`}>Delivery problem</button>
      </div>

      {error && <div role="alert" className="mt-4 flex gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"><AlertTriangle className="h-5 w-5 shrink-0" /><span>{error}</span></div>}
      {!online && <div role="status" className="mt-4 flex gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"><WifiOff className="h-5 w-5 shrink-0" /><span>You are offline. {targetV2 ? "A selected photo is encrypted on this device for up to 24 hours and cannot move to another device." : "A selected photo remains only on this screen."} Nothing is marked delivered until proof finalization and delivery confirmation succeed.</span></div>}

      {mode === "proof" ? (
        <div className="mt-5 space-y-4">
          {!proof && <>
            <label className="block">
              <span className="text-sm font-semibold">Proof photo</span>
              <span className="mt-1 block text-xs text-stone-500 dark:text-stone-400">JPEG, PNG, or WebP; up to 10 MB. Photograph the parcel at the destination, not a person.</span>
              <input className="sr-only" type="file" accept={(RIDER_PROOF_MIME_TYPES || ["image/jpeg", "image/png", "image/webp"]).join(",")} capture="environment" onChange={(event) => selectFile(event.target.files?.[0])} />
              <span className="mt-3 flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-2xl border border-violet-300 bg-white px-4 font-semibold text-violet-800 dark:border-violet-500/30 dark:bg-neutral-900 dark:text-violet-200"><Camera className="h-5 w-5" /> {file ? "Change or retake photo" : "Take or choose photo"}</span>
            </label>
            {previewUrl && <div className="relative overflow-hidden rounded-2xl border border-violet-200 bg-black/5 dark:border-violet-500/20"><img src={previewUrl} alt="Selected delivery proof preview" className="max-h-80 w-full object-contain" /><button type="button" onClick={async () => { if (pendingBinding) await deletePendingDeliveryProof(pendingBinding).catch(() => {}); setFile(null); setPreviewUrl(""); }} aria-label="Remove selected photo" className="absolute right-2 top-2 grid h-11 w-11 place-items-center rounded-full bg-black/70 text-white"><X className="h-5 w-5" /></button></div>}

            <div>
              <p className="text-sm font-semibold">Location evidence</p>
              <button type="button" disabled={locating} onClick={captureLocation} className="mt-2 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-violet-300 bg-white px-4 font-semibold text-violet-800 disabled:opacity-50 dark:border-violet-500/30 dark:bg-neutral-900 dark:text-violet-200">{locating ? <Loader2 className="h-5 w-5 animate-spin" /> : location ? <RefreshCw className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}{locating ? "Finding your location…" : location ? "Capture location again" : "Capture location"}</button>
              {locationInfo && <p className={`mt-2 rounded-xl p-3 text-sm ${locationInfo.tone === "emerald" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200" : "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200"}`}>{locationInfo.text}</p>}
              {location?.accuracyM > 150 && <label className="mt-3 flex min-h-12 items-start gap-3 rounded-xl border border-amber-300 p-3 text-sm dark:border-amber-500/30"><input type="checkbox" checked={acceptPoorGps} onChange={(event) => setAcceptPoorGps(event.target.checked)} className="mt-1 h-5 w-5 shrink-0" /><span>I understand that this proof will require review because location accuracy is poor.</span></label>}
            </div>

            {targetV2 ? <div className="space-y-3 rounded-2xl border border-violet-200 bg-white/70 p-4 dark:border-violet-500/20 dark:bg-black/10">
              <label className="block text-sm font-semibold">Who received the parcel?<select value={recipientType} onChange={(event) => { setRecipientType(event.target.value); setRecipientName(""); setRecipientRelationship(""); setReceivingLocation(""); setAuthorizationEvidence(""); }} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"><option value="">Choose recipient type</option><option value="CUSTOMER">Customer</option><option value="HOUSEHOLD_MEMBER">Household member</option><option value="GUARD_RECEPTION">Guard or reception</option><option value="AUTHORIZED_REPRESENTATIVE">Authorized representative</option></select></label>
              {recipientType && recipientType !== "CUSTOMER" && <label className="block text-sm font-semibold">Recipient name<input value={recipientName} onChange={(event) => setRecipientName(event.target.value)} maxLength={160} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>}
              {recipientType === "HOUSEHOLD_MEMBER" && <label className="block text-sm font-semibold">Relationship to customer<input value={recipientRelationship} onChange={(event) => setRecipientRelationship(event.target.value)} maxLength={120} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>}
              {recipientType === "GUARD_RECEPTION" && <label className="block text-sm font-semibold">Receiving location<input value={receivingLocation} onChange={(event) => setReceivingLocation(event.target.value)} maxLength={200} placeholder="Guardhouse, reception desk…" className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>}
              {recipientType === "AUTHORIZED_REPRESENTATIVE" && <label className="block text-sm font-semibold">Authorization evidence<textarea value={authorizationEvidence} onChange={(event) => setAuthorizationEvidence(event.target.value)} maxLength={500} rows={2} placeholder="Describe the customer’s authorization" className="mt-2 w-full rounded-2xl border border-stone-300 bg-white p-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>}
            </div> : <label className="block text-sm font-semibold">Recipient name <span className="font-normal text-stone-500">(optional)</span><input value={recipientName} onChange={(event) => setRecipientName(event.target.value)} maxLength={160} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>}
            <label className="block text-sm font-semibold">Delivery note <span className="font-normal text-stone-500">(optional)</span><textarea value={recipientNote} onChange={(event) => setRecipientNote(event.target.value)} maxLength={500} rows={3} className="mt-2 w-full rounded-2xl border border-stone-300 bg-white p-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>
            <button type="button" disabled={Boolean(busy) || !file || !location || !online || !recipientValid || !executionEnabled} onClick={uploadProof} className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-violet-700 px-5 text-lg font-semibold text-white disabled:opacity-50">{busy === "upload" ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />}{busy === "upload" ? stageText : "Finalize proof"}</button>
          </>}

          {proof && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10"><div className="flex gap-3"><CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-700 dark:text-emerald-300" /><div><p className="font-semibold">Proof finalized</p><p className="mt-1 text-sm text-stone-600 dark:text-stone-300">The photo is saved as append-only evidence{proof.reviewRequired ? " and is queued for review" : ""}. The parcel is not marked delivered yet.</p></div></div></div>}

          {proof && stop.isCashOnDelivery && <div className="space-y-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
            <div className="flex items-center justify-between gap-3"><span>{targetV2 ? "Amount due" : "Expected cash"}</span><strong>{formatPhp(expectedCashCents)}</strong></div>
            {!targetV2 && <div className="flex items-center justify-between gap-3"><span>COD status</span><strong>{stop.cod?.status || "MISSING"}</strong></div>}
            {stop.cod?.status === "MISSING" ? <p role="alert">This legacy shipment has no COD expectation. Contact dispatch; the Rider cannot create one at delivery.</p> : targetV2 && stop.cod?.status !== "DUE" ? null : <label className="block font-semibold">{targetV2 ? "Cash handed over by the customer" : "Cash actually received"}
              <span className="mt-2 flex min-h-12 items-center rounded-2xl border border-amber-300 bg-white px-4 text-stone-950 dark:border-amber-500/30 dark:bg-neutral-900 dark:text-white"><span className="mr-2">₱</span><input inputMode="decimal" value={cashAmount} onChange={(event) => { setCashAmount(event.target.value); setConfirmDelivery(false); }} className="min-w-0 flex-1 bg-transparent outline-none" aria-label="Cash actually received in Philippine pesos" /></span>
            </label>}
            {targetV2 ? <>
              {cashDifferenceCents !== null && cashDifferenceCents > 0 && <p className="rounded-xl border border-amber-400 bg-white/70 p-3 font-medium dark:bg-black/20">Give {formatPhp(cashDifferenceCents)} change back before you confirm.</p>}
              {cashDifferenceCents !== null && cashDifferenceCents < 0 && <p role="alert" className="rounded-xl border border-amber-400 bg-white/70 p-3 font-medium dark:bg-black/20">That is {formatPhp(Math.abs(cashDifferenceCents))} short of the amount due. Part payment is not accepted, so record a delivery problem instead.</p>}
              {stop.cod?.status === "COLLECTED" && <p className="rounded-xl border border-emerald-400 bg-white/70 p-3 font-medium dark:bg-black/20">Cash recorded. You are carrying {formatPhp(Number(stop.cod?.collectedAmountCents || 0))} for the store.</p>}
            </> : (cashDifferenceCents !== null && cashDifferenceCents !== 0 && <p role="alert" className="rounded-xl border border-amber-400 bg-white/70 p-3 font-medium dark:bg-black/20">{cashDifferenceCents < 0 ? "Short" : "Over"} by {formatPhp(Math.abs(cashDifferenceCents))}. Delivery may continue, but Finance cannot reconcile this record until an authorized discrepancy action resolves it.</p>)}
          </div>}

            {proof && targetV2 && <button type="button" disabled={Boolean(busy) || !online || !executionEnabled || (stop.isCashOnDelivery && (stop.cod?.status === "DUE" ? (!cashAmountCents || cashAmountCents < expectedCashCents || !stop.cod?.version) : stop.cod?.status !== "COLLECTED"))} onClick={deliverTarget} className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-5 text-lg font-semibold text-white disabled:opacity-50">{busy === "complete" ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}{stop.isCashOnDelivery && stop.cod?.status === "DUE" ? `Delivered — ${formatPhp(expectedCashCents)} received` : "Confirm delivered"}</button>}
            {proof && targetV2 && stop.isCashOnDelivery && stop.cod?.status === "DUE" && <p className="text-xs text-stone-600 dark:text-stone-300">One tap records the cash, then marks the parcel delivered. If the delivery step fails, the cash stays recorded.</p>}
            {proof && !targetV2 && (confirmDelivery ? <div className="rounded-2xl border border-emerald-300 bg-white p-4 dark:border-emerald-500/30 dark:bg-neutral-900"><p className="font-semibold">Confirm delivered{stop.isCashOnDelivery ? " and cash collected" : ""}?</p><p className="mt-1 text-sm text-stone-600 dark:text-stone-300">{stop.isCashOnDelivery ? (targetV2 ? "The cash is already recorded. This delivers the parcel; it does not record money again, and it does not mean the cash was remitted or reconciled." : `This records ${formatPhp(cashAmountCents)} as actual cash collected and delivers the parcel. It does not mean the cash was remitted or reconciled.`) : "This final action updates the attempt, stop, shipment, assignment, and order records."}</p><div className="mt-4 grid grid-cols-2 gap-3"><button type="button" disabled={Boolean(busy)} onClick={() => setConfirmDelivery(false)} className="min-h-12 rounded-2xl border border-stone-300 font-semibold dark:border-white/15">Back</button><button type="button" disabled={Boolean(busy) || !online || !executionEnabled} onClick={completeDelivery} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-700 font-semibold text-white disabled:opacity-50">{busy === "complete" ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Confirm delivered</button></div></div> : <button type="button" disabled={(stop.isCashOnDelivery && (targetV2 ? stop.cod?.status !== "COLLECTED" : (!cashAmountCents || stop.cod?.status !== "DUE" || !stop.cod?.version))) || !executionEnabled} onClick={() => setConfirmDelivery(true)} className="min-h-14 w-full rounded-2xl bg-emerald-700 px-5 text-lg font-semibold text-white disabled:opacity-50">{targetV2 && stop.isCashOnDelivery && stop.cod?.status !== "COLLECTED" ? "Record the cash first" : "Continue to delivery confirmation"}</button>)}
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <label className="block text-sm font-semibold">What happened?<select value={reason} onChange={(event) => { setReason(event.target.value); setNextAction(""); }} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"><option value="">Choose a problem</option>{FAILURE_REASONS.filter(([code]) => code !== "COD_NOT_PROVIDED" || stop.isCashOnDelivery).map(([code, text]) => <option key={code} value={code}>{text}</option>)}</select></label>
          {reason === "OTHER" && <label className="block text-sm font-semibold">Explain the problem<textarea required minLength={10} maxLength={1000} value={explanation} onChange={(event) => setExplanation(event.target.value)} rows={3} className="mt-2 w-full rounded-2xl border border-stone-300 bg-white p-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /><span className="mt-1 block text-xs font-normal text-stone-500">At least 10 characters.</span></label>}
          {reason && (targetV2
            ? <p className="rounded-2xl border border-stone-300 bg-white p-4 text-sm text-stone-700 dark:border-white/15 dark:bg-neutral-900 dark:text-stone-300">Dispatch decides what happens to this stop. Record what you saw; you keep the parcel until someone authorizes the next step.</p>
            : <label className="block text-sm font-semibold">What should happen next?<select value={nextAction} onChange={(event) => { setNextAction(event.target.value); setCustodyConfirmed(false); }} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"><option value="">Choose next action</option>{actions.map((action) => <option key={action} value={action}>{ACTION_LABELS[action]}</option>)}</select></label>)}

          <fieldset><legend className="text-sm font-semibold">Contact outcome <span className="font-normal text-stone-500">(optional)</span></legend><div className="mt-2 grid gap-2">{CONTACT_OUTCOMES.map(([code, text]) => <label key={code} className="flex min-h-11 items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 text-sm dark:border-white/10 dark:bg-neutral-900"><input type="checkbox" checked={contacts.includes(code)} onChange={() => toggleContact(code)} className="h-5 w-5 shrink-0" />{text}</label>)}</div></fieldset>

          {!targetV2 && nextAction === "RESCHEDULE" && <div className="grid gap-3 sm:grid-cols-2"><label className="text-sm font-semibold">Window starts<input type="datetime-local" max={RESCHEDULE_INPUT_MAX} value={rescheduledNotBefore} onChange={(event) => setRescheduledNotBefore(event.target.value)} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-3 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label><label className="text-sm font-semibold">Window ends<input type="datetime-local" max={RESCHEDULE_INPUT_MAX} value={rescheduledNotAfter} onChange={(event) => setRescheduledNotAfter(event.target.value)} className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-3 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label></div>}

          {!targetV2 && needsCustodyReturn && <label className="flex min-h-14 items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"><input type="checkbox" checked={custodyConfirmed} onChange={(event) => setCustodyConfirmed(event.target.checked)} className="mt-0.5 h-5 w-5 shrink-0" /><span><strong>Custody confirmation:</strong> I returned the parcel to dispatch. This action must not be submitted while I still hold the parcel.</span></label>}

          <label className="block text-sm font-semibold">Operational note <span className="font-normal text-stone-500">(optional)</span><textarea maxLength={1000} value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} className="mt-2 w-full rounded-2xl border border-stone-300 bg-white p-4 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white" /></label>
          <button type="button" disabled={Boolean(busy) || !executionEnabled || !reason || (!targetV2 && !nextAction) || (reason === "OTHER" && explanation.trim().length < 10) || (!targetV2 && needsCustodyReturn && !custodyConfirmed) || (!targetV2 && nextAction === "RESCHEDULE" && (!rescheduledNotBefore || !rescheduledNotAfter))} onClick={submitFailure} className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-amber-700 px-5 text-lg font-semibold text-white disabled:opacity-50">{busy === "failure" ? <Loader2 className="h-5 w-5 animate-spin" /> : <RotateCcw className="h-5 w-5" />} Record delivery problem</button>
        </div>
      )}
    </section>
  );
}
